import { PRODUCT_TYPE_MAP } from '@algolia-search/consts';
import type { AlgoliaProduct } from '@algolia-search/types';
import type { CurrencyEnum, Money } from 'modules/GraphQL/types';

import { useStore } from '~/composables';
import type { CategoryInterface } from '~/modules/catalog/category/types';
import type { Product } from '~/modules/catalog/product/types';

/**
 * Allowed image extensions for product images.
 */
const ALLOWED_IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp)$/i;

/**
 * Returns the product name or an empty string if not available.
 */
export const getProductName = (product: AlgoliaProduct): string => product?.name ?? '';

/**
 * Returns the product description or an empty string if not available.
 */
export const getProductDescription = (product: AlgoliaProduct): string => product?.plp_description ?? '';

/**
 * Returns the main image URL or an empty string if not available.
 */
export const getThumbnailUrl = (product: AlgoliaProduct): string => product?.thumbnail ?? '';

/**
 * Returns the main image URL or an empty string if not available.
 */
export const getImageUrl = (product: AlgoliaProduct): string => product?.image_url ?? '';

/**
 * Returns the hover image URL or null if not available or not a valid image.
 */
export const getHoverImageUrl = (product: AlgoliaProduct): string | null => {
  const baseUrl = process.env.VSF_MAGENTO_BASE_URL;
  const hoverPath = product?.hover_on_card;

  if (!baseUrl || !hoverPath || !ALLOWED_IMAGE_EXTENSIONS.test(hoverPath)) {
    return null;
  }

  // Ensure exactly one slash between '/media/catalog/product' and the hover_on_card path
  return `${baseUrl.replace(/\/$/, '')}/media/catalog/product/${hoverPath.replace(/^\//, '')}`;
};

/**
 * Returns the unique identifier for the product or an empty string if not available.
 */
export const getUid = (product: AlgoliaProduct): string => product?.objectID ?? '';

/**
 * Returns the product url_key (not a full URL) for routing.
 */
export const getProductUrlKey = (product: AlgoliaProduct): string => product?.url_key ?? '';

/**
 * Returns the product full URL for routing.
 */
export const getProductUrl = (product: AlgoliaProduct): string => {
  const url = product?.url ?? '';
  const parts = url.split('/').filter(Boolean);
  const productLink = parts[parts.length - 1] || '';
  return productLink ? `/p/${productLink}.html` : '';
};

/**
 * Returns the product price for the given currency.
 */
export const getProductPrice = (product: AlgoliaProduct, localeCurrency: CurrencyEnum): Money => {
  return {
    currency: localeCurrency,
    value: product.price?.[localeCurrency]?.default ?? 0
  };
};

/**
 * Returns the product price including taxes for the given currency.
 */
export const getProductPriceWithTax = (product: AlgoliaProduct, localeCurrency: CurrencyEnum): Money => {
  return {
    currency: localeCurrency,
    value: product.price_with_tax?.[localeCurrency]?.default ?? 0
  };
};

/**
 * Returns the bundle product minimum price.
 */
export const getBundleMinPrice = (product: AlgoliaProduct): number => {
  return product.custom_set_min_price || 0;
};

/**
 * Returns true if the product has a dynamic price for the given currency.
 */
export const hasDynamicPrice = (product: AlgoliaProduct, localeCurrency: CurrencyEnum): boolean => {
  return !!product.price?.[localeCurrency]?.default_max || product.price_type === 'DYNAMIC';
};

/**
 * Returns the mapped product type.
 */
export const getProductType = (product: AlgoliaProduct): string => PRODUCT_TYPE_MAP[product.type_id];

/**
 * Returns product stock status.
 */
export const getStockStatus = (product: AlgoliaProduct): string => product?.stock_status ?? 'OUT_OF_STOCK';

/**
 * Helper to build a price_range object for a product.
 */
const buildPriceRange = (product: AlgoliaProduct, localeCurrency: CurrencyEnum, isTaxIncluded = false) => {
  const productPrice = isTaxIncluded ? getProductPriceWithTax(product, localeCurrency) : getProductPrice(product, localeCurrency);

  return {
    minimum_price: {
      regular_price: productPrice,
      final_price: productPrice
    },
    maximum_price: {
      final_price: productPrice,
      regular_price: productPrice
    }
  };
};

/**
 * Type-safe value preparation for AlgoliaProduct fields.
 */
const prepareValue = <K extends keyof AlgoliaProduct>(product: AlgoliaProduct, key: K) => {
  if (!product || !product[key]) {
    return null;
  }
  const value = product[key];
  if (Array.isArray(value) && value.length) {
    return [
      {
        label: value[0],
        value: ''
      }
    ];
  }
  if (typeof value === 'string') {
    return [
      {
        label: value,
        value: ''
      }
    ];
  }
  return null;
};

/**
 * Maps Algolia category strings to CategoryInterface objects (minimal mapping).
 */
const mapAlgoliaCategoriesToCategoryInterfaces = (categoryUids?: string[]): CategoryInterface[] => {
  if (!categoryUids) return [];
  return categoryUids.map((uid) => ({ uid } as CategoryInterface));
};

/**
 * Maps an AlgoliaProduct to a Product type for compatibility with Magento-based UI components.
 * @param algoliaProduct The AlgoliaProduct to map
 * @param localeCurrency The currency to use for price fields
 * @param isJpStore Indicates whether JP store is active
 * @returns Product-compatible object
 */
export function mapAlgoliaProductToProduct(algoliaProduct: AlgoliaProduct, localeCurrency: CurrencyEnum, isJpStore: boolean): Product {
  const sku = Array.isArray(algoliaProduct.sku) ? algoliaProduct.sku[0] : algoliaProduct.sku;

  return {
    sku,
    url_key: getProductUrlKey(algoliaProduct),
    price_range: buildPriceRange(algoliaProduct, localeCurrency, isJpStore),
    price_excluded_tax: buildPriceRange(algoliaProduct, localeCurrency).maximum_price.final_price.value.toString(),
    uid: getUid(algoliaProduct),
    variant_name: prepareValue(algoliaProduct, 'variant_name'),
    editorial: prepareValue(algoliaProduct, 'editorial'),
    capacity: prepareValue(algoliaProduct, 'capacity'),
    plp_description: getProductDescription(algoliaProduct),
    categories: mapAlgoliaCategoriesToCategoryInterfaces(algoliaProduct.categories?.level0),
    title: getProductName(algoliaProduct),
    name: getProductName(algoliaProduct),
    thumbnail: {
      url: getThumbnailUrl(algoliaProduct)
    },
    hover_on_card: getHoverImageUrl(algoliaProduct),
    __typename: getProductType(algoliaProduct),
    stock_status: getStockStatus(algoliaProduct),
    is_salable: algoliaProduct.is_salable ?? false,
    isEngravingEnabled: algoliaProduct.isEngravingEnabled ?? false,
    preorder: algoliaProduct.preorderButtonLabel ?? false,
    preorderButtonLabel: algoliaProduct.preorderButtonLabel ?? '',
    revamp_related_products: algoliaProduct.revamp_related_products ?? [],
    custom_set_minimal_price: algoliaProduct.custom_set_min_price ?? 0,
    custom_set_minimal_price_excluded_tax: algoliaProduct.custom_set_min_price_excluded_tax ?? 0,
    price_type: algoliaProduct.price_type ?? '',
    redirect_code: 0
  } as unknown as Product;
}
